import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { clsx } from 'clsx'
import {
  Search, BookOpen, ArrowLeft, Info, Target, ListChecks, Code2, CheckCircle2,
  Clock, Wrench, ChevronRight, Link2,
} from 'lucide-react'
import {
  KB_ENTRIES, getKbEntry, KB_CATEGORIES, IMPACT_META, DIFFICULTY_META,
  type KbCategory, type KbEntry,
} from '../data/knowledgeBase'
import { useSeoHead, SITE } from '../lib/seo'
import { resolveHelpSlug, helpPath, seg } from '../lib/localizedUrl'

const badge: Record<string, string> = {
  red: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
}

export default function NapovedaPage() {
  const { id } = useParams()
  const { i18n } = useTranslation()
  const location = useLocation()
  const lang = i18n.language || 'cs'

  if (id) {
    // URL id může být lokalizovaný i starý český → kanonický kb id.
    const czId = resolveHelpSlug(id, lang)
    const entry = czId ? getKbEntry(czId) : undefined
    if (entry) {
      const want = helpPath(entry.id, lang)
      if (location.pathname !== want) return <Navigate to={want} replace />
      return <DocDetail entry={entry} />
    }
  }
  // Index — přesměruj starý segment na lokalizovaný (/napoveda → /help…).
  const wantIndex = `/${seg('help', lang)}`
  if (location.pathname !== wantIndex) return <Navigate to={wantIndex} replace />
  return <DocIndex />
}

// ── Index ──────────────────────────────────────────────────────────────────
function DocIndex() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language || 'cs'
  const [q, setQ] = useState('')
  const [cat, setCat] = useState<KbCategory | 'all'>('all')

  useSeoHead({
    title: `${t('kb.docs_title')} | SEO Tools`,
    description: t('kb.docs_subtitle'),
    canonicalPath: `/${seg('help', lang)}`,
    hreflangPath: (l) => `/${seg('help', l)}`,
    // ItemList odpovídá viditelnému výpisu témat nápovědy.
    jsonLd: [
      {
        '@context': 'https://schema.org', '@type': 'CollectionPage',
        name: `${t('kb.docs_title')} | SEO Tools`,
        url: SITE + `/${seg('help', lang)}`,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: KB_ENTRIES.length,
          itemListElement: KB_ENTRIES.map((e, i) => ({
            '@type': 'ListItem', position: i + 1, name: e.title,
            url: SITE + (lang === 'cs' ? '' : `/${lang}`) + helpPath(e.id, lang),
          })),
        },
      },
    ],
  })

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return KB_ENTRIES.filter((e) => {
      if (cat !== 'all' && e.category !== cat) return false
      if (!needle) return true
      return (e.title + ' ' + e.short + ' ' + e.what).toLowerCase().includes(needle)
    })
  }, [q, cat])

  const cats = Object.keys(KB_CATEGORIES) as KbCategory[]

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
          <BookOpen className="w-3.5 h-3.5" /> {t('kb.docs_title')}
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('kb.docs_title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 max-w-2xl">{t('kb.docs_subtitle')}</p>
      </motion.div>

      {/* Hledání + filtr */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t('kb.search_placeholder')}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <FilterChip active={cat === 'all'} onClick={() => setCat('all')}>{t('kb.all_categories')}</FilterChip>
          {cats.map((c) => (
            <FilterChip key={c} active={cat === c} onClick={() => setCat(c)}>{KB_CATEGORIES[c].label}</FilterChip>
          ))}
        </div>
      </div>

      {filtered.length === 0 && <p className="text-gray-400 text-center py-12">{t('kb.nothing_found')}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filtered.map((e, i) => {
          const impact = IMPACT_META[e.impact]
          return (
            <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.3) }}>
              <Link to={helpPath(e.id, lang)}
                className="group block h-full rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-white/5 p-4 hover:shadow-md hover:border-primary-200 transition">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300">{KB_CATEGORIES[e.category].label}</span>
                  <span className={clsx('text-[10px] font-semibold px-2 py-0.5 rounded-full', badge[impact.color])}>{impact.label}</span>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm group-hover:text-primary-600 transition flex items-center gap-1">
                  {e.title}
                  <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition" />
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{e.short}</p>
              </Link>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

// ── Detail ─────────────────────────────────────────────────────────────────
function DocDetail({ entry }: { entry: KbEntry }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language || 'cs'
  const navigate = useNavigate()
  const impact = IMPACT_META[entry.impact]
  const diff = DIFFICULTY_META[entry.difficulty]
  const cat = KB_CATEGORIES[entry.category]

  // Self-referencing canonical + hreflang per jazyk (lokalizované URL).
  const prefix = lang === 'cs' ? '' : `/${lang}`
  const entryUrl = SITE + prefix + helpPath(entry.id, lang)
  useSeoHead({
    title: `${entry.title} | ${t('kb.docs_title')} | SEO Tools`,
    description: entry.short,
    canonicalPath: helpPath(entry.id, lang),
    hreflangPath: (l) => helpPath(entry.id, l),
    // TechArticle odpovídá viditelnému výkladovému obsahu (co/proč/jak) → posílá E-E-A-T a Article rich výsledky.
    jsonLd: [
      {
        '@context': 'https://schema.org', '@type': 'TechArticle',
        headline: entry.title,
        description: entry.short,
        inLanguage: lang,
        mainEntityOfPage: entryUrl,
        author: { '@type': 'Organization', name: 'SEO Tools', url: SITE + '/' },
        publisher: { '@type': 'Organization', name: 'SEO Tools', url: SITE + '/' },
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('kb.docs_title'), item: SITE + prefix + `/${seg('help', lang)}` },
          { '@type': 'ListItem', position: 2, name: entry.title, item: entryUrl },
        ],
      },
    ],
  })

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link to={`/${seg('help', lang)}`} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary-600 mb-6">
        <ArrowLeft className="w-4 h-4" /> {t('kb.docs_title')}
      </Link>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300">{cat.label}</span>
          <span className={clsx('inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full', badge[impact.color])}>
            <Target className="w-3 h-3" /> {impact.label}
          </span>
          <span className={clsx('inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full', badge[diff.color])}>
            <Wrench className="w-3 h-3" /> {diff.label}
          </span>
          {entry.effort && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300">
              <Clock className="w-3 h-3" /> {entry.effort}
            </span>
          )}
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 leading-tight">{entry.title}</h1>
        <p className="text-lg text-gray-500 dark:text-gray-400 mb-8">{entry.short}</p>
      </motion.div>

      <div className="space-y-8">
        <DocSection icon={<Info className="w-5 h-5" />} color="indigo" title={t('kb.what')}>
          <p>{entry.what}</p>
        </DocSection>

        <DocSection icon={<Target className="w-5 h-5" />} color="rose" title={t('kb.why')}>
          <p>{entry.why}</p>
        </DocSection>

        <DocSection icon={<ListChecks className="w-5 h-5" />} color="emerald" title={t('kb.how')}>
          <ol className="space-y-3 not-prose">
            {entry.how.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <span className="text-gray-700 dark:text-gray-300">{s}</span>
              </li>
            ))}
          </ol>
        </DocSection>

        {entry.examples?.map((ex, i) => (
          <DocSection key={i} icon={<Code2 className="w-5 h-5" />} color="violet" title={ex.caption || t('kb.example')}>
            {ex.bad && <DocCode tone="bad" label={t('kb.bad')}>{ex.bad}</DocCode>}
            {ex.good && <DocCode tone="good" label={t('kb.good')}>{ex.good}</DocCode>}
            {ex.code && <DocCode tone="neutral">{ex.code}</DocCode>}
          </DocSection>
        ))}

        <div className="rounded-2xl border border-primary-200 dark:border-primary-500/30 bg-primary-50/70 dark:bg-primary-500/10 p-6">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-primary-600" />
            <h2 className="text-base font-bold text-primary-700 dark:text-primary-300">{t('kb.conclusion')}</h2>
          </div>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed">{entry.conclusion}</p>
        </div>

        {entry.related && entry.related.length > 0 && (
          <div className="pt-2">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
              <Link2 className="w-4 h-4" /> {t('kb.related')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {entry.related.map((rid) => {
                const r = getKbEntry(rid)
                if (!r) return null
                return (
                  <button key={rid} onClick={() => navigate(helpPath(rid, lang))}
                    className="text-left text-sm px-4 py-3 rounded-xl border border-gray-100 dark:border-white/10 bg-white dark:bg-white/5 hover:border-primary-200 hover:shadow-sm transition flex items-center justify-between gap-2">
                    <span className="text-gray-700 dark:text-gray-300">{r.title}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function DocSection({ icon, color, title, children }: { icon: React.ReactNode; color: string; title: string; children: React.ReactNode }) {
  const colors: Record<string, string> = {
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300',
    rose: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
    violet: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
  }
  return (
    <section>
      <div className="flex items-center gap-2.5 mb-3">
        <span className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', colors[color])}>{icon}</span>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
      </div>
      <div className="pl-0 sm:pl-12 text-gray-700 dark:text-gray-300 leading-relaxed">{children}</div>
    </section>
  )
}

function DocCode({ tone, label, children }: { tone: 'bad' | 'good' | 'neutral'; label?: string; children: string }) {
  const tones = {
    bad: 'border-red-200 bg-red-50/60 dark:border-red-500/30 dark:bg-red-500/5',
    good: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-500/30 dark:bg-emerald-500/5',
    neutral: 'border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/5',
  }
  const labelTones = {
    bad: 'text-red-600 dark:text-red-400',
    good: 'text-emerald-600 dark:text-emerald-400',
    neutral: 'text-gray-500',
  }
  return (
    <div className={clsx('rounded-xl border mb-3 overflow-hidden', tones[tone])}>
      {label && <div className={clsx('px-4 pt-2.5 text-[10px] font-bold uppercase tracking-wider', labelTones[tone])}>{label}</div>}
      <pre className="px-4 py-3 text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap break-words leading-relaxed"><code>{children}</code></pre>
    </div>
  )
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={clsx('text-xs font-medium px-3 py-2 rounded-xl transition',
        active ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200')}>
      {children}
    </button>
  )
}
